import { Alert, Button, Form, Input, message, Space, Typography } from 'antd';
import { useState } from 'react';
import { Link, Navigate, useLoaderData } from 'react-router-dom';
import { getPasswordReset, resetPassword } from '../api/allauth';
import { ROUTES } from '../routes';
import styles from "./Accounts.module.css";

const { Title, Text } = Typography

export async function resetPasswordByLinkLoader({ params }) {
    const key = params.key
    const resp = await getPasswordReset(key)
    return { resetKey: key, resetKeyResponse: resp }
}

function ResetPassword({ resetKey, resetKeyResponse }) {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const [response, setResponse] = useState({ fetching: false, content: null })

    const onFinish = async (values) => {
        setLoading(true)
        setResponse({ ...response, fetching: true })

        try {
            const resp = await resetPassword({
                key: resetKey,
                password: values.password
            })
            setResponse({ ...response, content: resp })
        } catch (e) {
            console.error("Password reset failed:", e);
            message.error("Could not reset password due to network issues. Please, contact support if problem persists.");
        } finally {
            setLoading(false)
            setResponse(r => ({ ...r, fetching: false }))
        }
    }

    if ([200, 401].includes(response.content?.status)) {
        return <Navigate to={ROUTES.ACCOUNT.LOGIN} />
    }

    // Error display component
    const FormErrors = ({ param, errors }) => {
        const errorMessages = errors?.filter(e => e.param === param)
            .map(e => e.message)

        if (!errorMessages?.length) return null

        return (
            <Alert
                message={errorMessages.join(', ')}
                type="error"
                showIcon
                style={{ marginTop: 8 }}
            />
        )
    }

    let body

    if (resetKeyResponse.status !== 200) {
        body = <FormErrors param='key' errors={resetKeyResponse.errors} />
    } else if (response.content?.errors?.some(e => e.param === 'key')) {
        body = <FormErrors param='key' errors={response.content?.errors} />
    } else {
        body = (
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                autoComplete="off"
            >
                <Form.Item
                    label="New Password"
                    name="password"
                    rules={[
                        { required: true, message: 'Please input your new password!' },
                        { min: 6, message: 'Password must be at least 6 characters!' }
                    ]}
                >
                    <Input.Password
                        autoComplete="new-password"
                        placeholder="Enter new password"
                    />
                </Form.Item>

                <Form.Item
                    label="Confirm Password"
                    name="confirmPassword"
                    dependencies={['password']}
                    rules={[
                        { required: true, message: 'Please confirm your password!' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                    return Promise.resolve()
                                }
                                return Promise.reject(new Error('Passwords do not match!'))
                            },
                        }),
                    ]}
                >
                    <Input.Password
                        placeholder="Confirm new password"
                    />
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                        size="large"
                    >
                        Reset Password
                    </Button>
                </Form.Item>
            </Form>
        )
    }

    return (
        <div className={styles.card}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div style={{ textAlign: 'center' }}>
                    <img src="/w5n-logo.svg" alt="Logo" className={styles.logo} />
                    <Title level={2}>Reset Password</Title>
                </div>

                {body}

                <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <Text type="secondary">
                        Remember your password?{' '}
                        <Link to={ROUTES.ACCOUNT.LOGIN}>Back to login</Link>
                    </Text>
                </div>
            </Space>
        </div>
    )
}

export function ResetPasswordByLink() {
    const { resetKey, resetKeyResponse } = useLoaderData()
    return <ResetPassword resetKey={resetKey} resetKeyResponse={resetKeyResponse} />
}